<template>
  <div class="header">
    <div class="header-left">
      <button class="collapse-btn" @click="$emit('toggle-sidebar')">
        <el-icon><Menu /></el-icon>
      </button>
      <div class="breadcrumb">
        <span>{{ currentPageTitle }}</span>
      </div>
    </div>
    
    <div class="header-right">
      <!-- 语言切换 -->
      <div class="language-switcher">
        <button 
          @click="switchLanguage('zh-CN')" 
          :class="{ active: locale === 'zh-CN' }"
          class="lang-btn"
          type="button"
        >
          中文
        </button>
        <button 
          @click="switchLanguage('en-US')" 
          :class="{ active: locale === 'en-US' }"
          class="lang-btn"
          type="button"
        >
          English
        </button>
      </div>
      
      <!-- 用户信息 -->
      <div class="user-info">
        <el-dropdown @command="handleCommand">
          <span class="user-dropdown">
            <el-icon><UserIcon /></el-icon>
            <span class="username">{{ userInfo?.username || 'Admin' }}</span>
            <el-icon><ArrowDown /></el-icon>
          </span>
          <template #dropdown>
            <el-dropdown-menu>
              <el-dropdown-item command="profile">
                <el-icon><UserIcon /></el-icon>
                {{ $t('header.profile') }}
              </el-dropdown-item>
              <el-dropdown-item command="logout" divided>
                <el-icon><SwitchButton /></el-icon>
                {{ $t('header.logout') }}
              </el-dropdown-item>
            </el-dropdown-menu>
          </template>
        </el-dropdown>
      </div>
    </div>
    
    <!-- 个人信息编辑弹窗 -->
    <el-dialog
      v-model="showProfileDialog"
      :title="isEmailRequired ? $t('profile.titleRequired') : $t('profile.title')"
      width="700px"
      :close-on-click-modal="!isEmailRequired"
      :close-on-press-escape="!isEmailRequired"
      :show-close="!isEmailRequired"
      @closed="handleDialogClosed"
    >
      <el-form
        ref="profileFormRef"
        :model="profileForm"
        :rules="profileRules"
        label-width="120px"
      >
        <el-form-item :label="$t('profile.username')">
          <el-input
            v-model="profileForm.username"
            disabled
          />
        </el-form-item>
        
        <el-form-item
          :label="$t('profile.currentPassword')"
          prop="password"
        >
          <el-input
            v-model="profileForm.password"
            type="password"
            :placeholder="$t('profile.currentPasswordPlaceholder')"
            show-password
          />
        </el-form-item>
        
        <el-form-item
          :label="$t('profile.newPassword')"
          prop="newPassword"
        >
          <el-input
            v-model="profileForm.newPassword"
            type="password"
            :placeholder="$t('profile.newPasswordPlaceholder')"
            show-password
          />
        </el-form-item>
        
        <el-form-item
          :label="$t('profile.confirmPassword')"
          prop="confirmPassword"
        >
          <el-input
            v-model="profileForm.confirmPassword"
            type="password"
            :placeholder="$t('profile.confirmPasswordPlaceholder')"
            show-password
          />
        </el-form-item>
        
        <el-form-item
          :label="$t('profile.email')"
          prop="newEmail"
        >
          <div class="email-input-group">
            <el-input
              v-model="profileForm.newEmail"
              type="email"
              :placeholder="$t('profile.emailPlaceholder')"
              :disabled="sendingCode || (userInfo && userInfo.email && userInfo.email.trim() !== '')"
            />
            <el-button
              type="primary"
              :disabled="!canSendCode || sendingCode || countdown > 0"
              @click="handleSendVerifyCode"
              class="send-code-btn"
            >
              <span v-if="countdown > 0">{{ countdown }}{{ $t('common.second') }}{{ $t('profile.resendAfter') }}</span>
              <span v-else-if="sendingCode">{{ $t('profile.sendingVerifyCode') }}</span>
              <span v-else>{{ $t('profile.sendVerifyCode') }}</span>
            </el-button>
          </div>
        </el-form-item>
        
        <el-form-item
          :label="$t('profile.verifyCode')"
          prop="verifyCode"
        >
          <el-input
            v-model="profileForm.verifyCode"
            :placeholder="$t('profile.verifyCodePlaceholder')"
            maxlength="6"
          />
        </el-form-item>
      </el-form>
      
      <template #footer>
        <span class="dialog-footer">
          <el-button
            v-if="!isEmailRequired"
            @click="showProfileDialog = false"
          >
            {{ $t('profile.cancel') }}
          </el-button>
          <el-button
            type="primary"
            :loading="profileLoading"
            @click="handleUpdateProfile"
          >
            {{ profileLoading ? $t('profile.saving') : $t('profile.save') }}
          </el-button>
        </span>
      </template>
    </el-dialog>
  </div>
</template>

<script>
import { ref, computed, onMounted, watch } from 'vue'
import { useRouter, useRoute } from 'vue-router'
import { useI18n } from 'vue-i18n'
import { Menu, User as UserIcon, ArrowDown, SwitchButton } from '@element-plus/icons-vue'
import { getUser, removeToken, setUser } from '@/utils/auth'
import axios from '@/utils/request'
import { ElMessage } from 'element-plus'

export default {
  name: 'AdminHeader',
  emits: ['toggle-sidebar'],
  setup() {
    const router = useRouter()
    const route = useRoute()
    const { t, locale } = useI18n()
    
    const userInfo = ref(null)
    const showProfileDialog = ref(false)
    const isEmailRequired = ref(false)
    const profileLoading = ref(false)
    const profileFormRef = ref(null)
    
    // 个人信息表单
    const profileForm = ref({
      username: '',
      password: '',
      newPassword: '',
      confirmPassword: '',
      newEmail: '',
      verifyCode: ''  // 新增：验证码
    })
    
    // 验证码相关状态
    const sendingCode = ref(false)
    const countdown = ref(0)
    
    // 表单验证规则
    const profileRules = computed(() => ({
      password: [
        { required: true, message: t('profile.currentPasswordRequired'), trigger: 'blur' }
      ],
      newPassword: [
        { required: true, message: t('profile.newPasswordRequired'), trigger: 'blur' },
        { min: 6, message: t('profile.passwordMinLength'), trigger: 'blur' },
        {
          validator: (rule, value, callback) => {
            if (value && profileForm.value.confirmPassword && value !== profileForm.value.confirmPassword) {
              callback(new Error(t('profile.passwordMismatch')))
            } else {
              callback()
            }
          },
          trigger: 'blur'
        }
      ],
      confirmPassword: [
        { required: true, message: t('profile.confirmPasswordRequired'), trigger: 'blur' },
        {
          validator: (rule, value, callback) => {
            if (!value || value.trim() === '') {
              callback(new Error(t('profile.confirmPasswordRequired')))
            } else if (profileForm.value.newPassword && value !== profileForm.value.newPassword) {
              callback(new Error(t('profile.passwordMismatch')))
            } else {
              callback()
            }
          },
          trigger: 'blur'
        }
      ],
      newEmail: [
        { required: true, message: t('profile.emailRequired'), trigger: 'blur' },
        {
          validator: (rule, value, callback) => {
            if (!value || value.trim() === '') {
              callback(new Error(t('profile.emailRequired')))
            } else if (!/^[A-Za-z0-9+_.-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,}$/.test(value)) {
              callback(new Error(t('profile.emailFormatError')))
            } else {
              callback()
            }
          },
          trigger: ['blur', 'change']
        }
      ],
      verifyCode: [
        { required: true, message: t('profile.verifyCodeRequired'), trigger: 'blur' },
        {
          validator: (rule, value, callback) => {
            if (!value || value.trim() === '') {
              callback(new Error(t('profile.verifyCodeRequired')))
            } else if (!/^\d{6}$/.test(value)) {
              callback(new Error(t('profile.verifyCodeLength')))
            } else {
              callback()
            }
          },
          trigger: 'blur'
        }
      ]
    }))
    
    // 判断是否可以发送验证码
    const canSendCode = computed(() => {
      return profileForm.value.newEmail && 
             /^[A-Za-z0-9+_.-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,}$/.test(profileForm.value.newEmail)
    })
    
    // 监听邮箱变化，如果邮箱改变，清空验证码和倒计时
    watch(() => profileForm.value.newEmail, (newEmail, oldEmail) => {
      // 如果邮箱改变（且不是初始加载），清空验证码和倒计时
      if (oldEmail !== undefined && newEmail !== oldEmail) {
        profileForm.value.verifyCode = ''
        countdown.value = 0
      }
    })
    
    // 发送验证码
    const handleSendVerifyCode = async () => {
      // 确定实际使用的邮箱：如果用户已有邮箱，使用数据库中的邮箱；否则使用表单中的邮箱
      const actualEmail = (userInfo.value && userInfo.value.email && userInfo.value.email.trim() !== '') 
        ? userInfo.value.email  // 非首次设置：使用数据库中的邮箱
        : profileForm.value.newEmail  // 首次设置：使用表单中的邮箱
      
      // 前端验证邮箱格式
      if (!actualEmail || !/^[A-Za-z0-9+_.-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,}$/.test(actualEmail)) {
        ElMessage.warning(t('profile.emailFormatError'))
        return
      }
      
      sendingCode.value = true
      try {
        // 获取当前语言，转换为后端参数（0=中文，1=英文）
        const language = locale.value === 'en-US' ? 1 : 0
        
        const response = await axios.post('/user/send-email-verify-code', {
          email: actualEmail,  // 使用实际邮箱（数据库中的或表单中的）
          language: language  // 新增：语言参数
        })
        
        if (response.code === 200) {
          ElMessage.success(t('profile.verifyCodeSent'))
          // 开始倒计时
          startCountdown()
        } else {
          // 根据错误码显示错误消息
          const errorMessage = getVerifyCodeErrorMessage(response.code)
          ElMessage.error(errorMessage)
        }
      } catch (error) {
        console.error('发送验证码失败:', error)
        // 根据错误码判断
        const errorCode = error.code || error.response?.data?.code
        const errorMessage = getVerifyCodeErrorMessage(errorCode)
        ElMessage.error(errorMessage)
      } finally {
        sendingCode.value = false
      }
    }
    
    // 倒计时逻辑
    const startCountdown = () => {
      countdown.value = 60
      const timer = setInterval(() => {
        countdown.value--
        if (countdown.value <= 0) {
          clearInterval(timer)
        }
      }, 1000)
    }
    
    // 根据错误码获取验证码相关的错误提示
    const getVerifyCodeErrorMessage = (code) => {
      switch (code) {
        case 4301:
          // 验证码错误或已过期
          return t('profile.errorVerifyCodeError')
        case 4302:
          // 验证码发送频率过高
          return t('profile.errorVerifyCodeRateLimit')
        case 4303:
          // 验证码发送失败
          return t('profile.errorVerifyCodeSendFailed')
        case 4003:
          // 邮箱格式不正确
          return t('profile.errorEmailFormat')
        default:
          // 默认错误消息
          return t('profile.verifyCodeSendFailed')
      }
    }
    
    // 获取用户信息
    const loadUserInfo = () => {
      const user = getUser()
      if (user) {
        userInfo.value = user
        profileForm.value.username = user.username || ''
        profileForm.value.newEmail = user.email || ''
      }
    }
    
    onMounted(() => {
      loadUserInfo()
      
      // 监听路由变化，进入仪表盘时检查邮箱
      // watch(() => route.path, (newPath) => {
      //   if (newPath === '/dashboard') {
      //     checkEmailRequired()
      //   }
      // }, { immediate: true })
      
      // 组件挂载时直接检查邮箱，确保任何页面刷新都会检查
      checkEmailRequired()
    })
    
    // 检查是否需要强制设置邮箱
    const checkEmailRequired = () => {
      const user = getUser()
      if (user && (!user.email || user.email.trim() === '')) {
        isEmailRequired.value = true
        showProfileDialog.value = true
        profileForm.value.newEmail = ''
      }
    }
    
    // 处理下拉菜单命令
    const handleCommand = (command) => {
      if (command === 'profile') {
        isEmailRequired.value = false
        // 打开弹窗时重新加载用户信息（确保邮箱是最新的）
        loadUserInfo()
        // 清空表单（除了用户名和邮箱，邮箱从用户信息中获取）
        resetProfileForm()
        showProfileDialog.value = true
      } else if (command === 'logout') {
        handleLogout()
      }
    }
    
    // 重置表单（保留用户名和邮箱，清空其他字段）
    const resetProfileForm = () => {
      const user = getUser()
      const currentEmail = user?.email || ''
      // 清空所有字段
      profileForm.value.password = ''
      profileForm.value.newPassword = ''
      profileForm.value.confirmPassword = ''
      profileForm.value.verifyCode = ''
      // 重置倒计时
      countdown.value = 0
      // 保留邮箱（从用户信息中获取）
      profileForm.value.newEmail = currentEmail
      // 清空表单验证状态
      if (profileFormRef.value) {
        profileFormRef.value.clearValidate()
      }
    }
    
    // 弹窗关闭时的处理
    const handleDialogClosed = () => {
      // 如果不是强制设置邮箱的情况，关闭时清空表单
      if (!isEmailRequired.value) {
        resetProfileForm()
      }
    }
    
    // 根据错误码获取对应的错误提示
    const getProfileErrorMessage = (code) => {
      // 根据不同的错误码返回对应的国际化消息
      switch (code) {
        case 401:
          // 未登录或登录已过期
          return t('profile.errorNotLoggedIn')
        case 403:
          // 只能修改自己的信息
          return t('profile.errorNotOwnInfo')
        case 4001:
          // 当前密码错误
          return t('profile.errorCurrentPasswordWrong')
        case 4002:
          // 新密码和确认密码不一致
          return t('profile.errorPasswordMismatch')
        case 4003:
          // 邮箱格式不正确
          return t('profile.errorEmailFormat')
        case 4004:
          // 密码长度至少6位
          return t('profile.errorPasswordTooShort')
        case 4301:
          // 验证码错误或已过期
          return t('profile.errorVerifyCodeError')
        case 4302:
          // 验证码发送频率过高
          return t('profile.errorVerifyCodeRateLimit')
        case 4303:
          // 验证码发送失败
          return t('profile.errorVerifyCodeSendFailed')
        case 500:
          // 服务器错误
          return t('profile.errorUpdateFailed')
        default:
          // 默认错误消息
          return t('profile.updateFailed')
      }
    }
    
    // 更新个人信息
    const handleUpdateProfile = async () => {
      if (!profileFormRef.value) return
      
      try {
        // 表单验证
        await profileFormRef.value.validate()
        
        // 前端校验：新密码和确认密码必须一致
        if (profileForm.value.newPassword !== profileForm.value.confirmPassword) {
          ElMessage.error(t('profile.passwordMismatch'))
          return
        }
        
        // 前端校验：新密码长度至少6位
        if (profileForm.value.newPassword && profileForm.value.newPassword.length < 6) {
          ElMessage.error(t('profile.passwordMinLength'))
          return
        }
        
        // 前端校验：邮箱格式
        const emailRegex = /^[A-Za-z0-9+_.-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,}$/
        if (profileForm.value.newEmail && !emailRegex.test(profileForm.value.newEmail)) {
          ElMessage.error(t('profile.emailFormatError'))
          return
        }
        
        // 前端校验：验证码格式
        if (!profileForm.value.verifyCode || !/^\d{6}$/.test(profileForm.value.verifyCode)) {
          ElMessage.error(t('profile.verifyCodeLength'))
          return
        }
        
        profileLoading.value = true
        
        const response = await axios.post('/user/update-profile', {
          username: profileForm.value.username,
          password: profileForm.value.password,
          newPassword: profileForm.value.newPassword,
          confirmPassword: profileForm.value.confirmPassword,
          newEmail: profileForm.value.newEmail,
          verifyCode: profileForm.value.verifyCode  // 新增：验证码
        })
        
        if (response.code === 200) {
          ElMessage.success(t('profile.updateSuccess'))
          
          // 更新本地用户信息
          if (response.data) {
            setUser(response.data)
            userInfo.value = response.data
            profileForm.value.newEmail = response.data.email || ''
          }
          
          // 修改成功后自动关闭弹窗
          if (isEmailRequired.value) {
            isEmailRequired.value = false
          }
          showProfileDialog.value = false
          
          // 清空表单（除了用户名和邮箱）
          resetProfileForm()
        } else {
          // 根据不同的错误码显示具体的错误原因
          const errorCode = response.code
          console.log('更新个人信息失败，错误码:', errorCode, '错误消息:', response.message)
          const errorMessage = getProfileErrorMessage(errorCode)
          ElMessage.error(errorMessage)
        }
      } catch (error) {
        console.error('更新个人信息失败:', error)
        // 根据错误码判断（优先从 error.code，其次从 error.response.data.code）
        const errorCode = error.code || error.response?.data?.code
        console.log('捕获到错误，错误码:', errorCode, '错误消息:', error.message || error.response?.data?.message)
        const errorMessage = getProfileErrorMessage(errorCode)
        ElMessage.error(errorMessage)
      } finally {
        profileLoading.value = false
      }
    }
    
    // 切换语言
    const switchLanguage = (lang) => {
      locale.value = lang
      // 保存语言设置到 localStorage
      localStorage.setItem('admin_locale', lang)
    }
    
    // 退出登录
    const handleLogout = async () => {
      try {
        await axios.post('/auth/logout')
      } catch (error) {
        console.error('退出登录失败:', error)
      } finally {
        removeToken()
        router.push('/login')
      }
    }
    
    return {
      locale,
      userInfo,
      currentPageTitle: computed(() => {
        const titleKey = route.meta?.title
        if (titleKey) {
          return t(titleKey)
        }
        return t('menu.dashboard')
      }),
      handleCommand,
      switchLanguage,
      showProfileDialog,
      isEmailRequired,
      profileForm,
      profileFormRef,
      profileRules,
      profileLoading,
      handleUpdateProfile,
      sendingCode,
      countdown,
      canSendCode,
      handleSendVerifyCode,
      handleDialogClosed
    }
  },
  components: {
    Menu,
    UserIcon,
    ArrowDown,
    SwitchButton
  }
}
</script>

<style lang="scss" scoped>
.header {
  height: 84px;
  background: #fff;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0 24px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  z-index: 999;
}

.header-left {
  display: flex;
  align-items: center;
  
  .collapse-btn {
    background: none;
    border: none;
    font-size: 20px;
    color: #666;
    cursor: pointer;
    padding: 8px;
    margin-right: 16px;
    transition: color 0.3s;
    
    &:hover {
      color: #1890ff;
    }
  }
  
  .breadcrumb {
    font-size: 16px;
    font-weight: 500;
    color: #262626;
  }
}

.header-right {
  display: flex;
  align-items: center;
  gap: 16px;
  
  .language-switcher {
    display: flex;
    align-items: center;
    gap: 0;
    background: #f5f5f5;
    padding: 4px;
    border-radius: 20px;
    box-shadow: 0 1px 4px rgba(0, 0, 0, 0.1);
  }
  
  .lang-btn {
    background: transparent;
    border: none;
    padding: 6px 16px;
    font-size: 14px;
    color: #666;
    cursor: pointer;
    transition: all 0.3s ease;
    border-radius: 16px;
    font-weight: 500;
    width: 70px;
    text-align: center;
    white-space: nowrap;
    box-sizing: border-box;
  }
  
  .lang-btn:hover:not(.active) {
    color: #1890ff;
    background: rgba(24, 144, 255, 0.08);
  }
  
  .lang-btn.active {
    color: #fff;
    font-weight: 500;
    background: linear-gradient(135deg, #1890ff 0%, #096dd9 100%);
    box-shadow: 0 2px 8px rgba(24, 144, 255, 0.3);
  }
  
  .lang-btn.active:hover {
    background: linear-gradient(135deg, #40a9ff 0%, #1890ff 100%);
    box-shadow: 0 4px 12px rgba(24, 144, 255, 0.4);
  }
  
  .user-info {
    .user-dropdown {
      display: flex;
      align-items: center;
      cursor: pointer;
      padding: 8px 12px;
      border-radius: 4px;
      transition: background 0.3s;
      
      &:hover {
        background: #f5f5f5;
      }
      
      .el-icon-user {
        font-size: 18px;
        color: #1890ff;
        margin-right: 8px;
      }
      
      .username {
        font-size: 14px;
        color: #262626;
        margin-right: 8px;
      }
      
      .el-icon-arrow-down {
        font-size: 12px;
        color: #8c8c8c;
      }
    }
  }
}

// 个人信息弹窗样式
:deep(.el-dialog) {
  .email-input-group {
    display: flex;
    gap: 8px;
    
    .el-input {
      flex: 1;
    }
    
    .send-code-btn {
      flex-shrink: 0;
      min-width: 120px;
    }
  }
}
</style>

